import { FC, useMemo, useState } from 'react';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { createColumnHelper } from '@tanstack/react-table';
import useAdminCarers, { Carer } from '@/api/useAdminCarers.ts';
import useAdminDeleteUser from '@/api/useAdminDeleteUser.ts';
import DeleteUserModal from '@/components/modals/DeleteUserModal.tsx';
import { TrashIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';

const columnHelper = createColumnHelper<Carer>();

const AdminCarersPage: FC = () => {
  const { data: carers, isLoading, isError } = useAdminCarers();
  const navigate = useNavigate();
  const { mutate: deleteUser, isPending: isDeleting, isError: isDeleteError, error: deleteError, reset: resetDelete } = useAdminDeleteUser();
  const [deleteTarget, setDeleteTarget] = useState<Carer | null>(null);

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteUser(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: 'Carer',
        cell: (info) => (
          <div className="font-semibold">
            {info.getValue().split('@')[0]}
          </div>
        ),
      }),
      columnHelper.accessor('created_at', {
        header: 'Acc creation date',
        cell: (info) => dayjs(info.getValue()).format('MMMM DD, YYYY — hh:mm A'),
      }),
      columnHelper.accessor('is_verified', {
        header: 'Status',
        cell: (info) => {
          const carer = info.row.original;
          return (
            <div className="flex items-center gap-2">
              <span className={`font-semibold ${info.getValue() ? 'text-[#00A96E]' : 'text-[#FF5861]'}`}>
                {info.getValue() ? 'Verified' : 'Not Verified'}
              </span>
              {carer.is_hidden && (
                <span className="text-xs bg-gray-200 text-gray-600 font-medium px-2 py-0.5 rounded-full">
                  Hidden
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Details',
        cell: (info) => (
          <button 
            onClick={() => navigate(Routes.ADMIN_CARER_DETAILS.replace(':carerId', info.row.original.id))}
            className="text-black hover:underline font-semibold"
          >
            Show details
          </button>
        ),
      }),
      columnHelper.display({
        id: 'delete',
        header: '',
        cell: (info) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              resetDelete();
              setDeleteTarget(info.row.original);
            }}
            className="p-2 text-gray-400 hover:text-[#FF5861] transition-colors cursor-pointer"
            title="Delete carer"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        ),
      }),
    ],
    [navigate, resetDelete]
  );

  return (
    <div className="flex flex-col gap-6 w-full max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Lite admin dashboard for daily management</h1>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-500">Loading...</p>
        </div>
      ) : isError ? (
        <div className="bg-red-50 rounded-lg p-8 text-center">
          <p className="text-red-500">Error loading carers</p>
        </div>
      ) : (
        <DataTable data={carers || []} columns={columns} className="min-w-[480px]" />
      )}

      <DeleteUserModal
        isOpen={!!deleteTarget}
        onClose={() => isDeleting ? null : setDeleteTarget(null)}
        onConfirm={handleDelete}
        userName={deleteTarget?.email ?? ''}
        userType="carer"
        isLoading={isDeleting}
        error={isDeleteError ? (deleteError as Error)?.message : null}
      />
    </div>
  );
};

export default AdminCarersPage;
